/**
 * Theme materialisation — the deterministic post-pass that bakes a tenant's
 * FULL derived brand theme into a scaffolded app.
 *
 * {{GHOSTY_*}} substitution (slug/name/domain/...) happens first, in the
 * layered gather. This pass runs AFTER, over the in-memory file list, and
 * REWRITES the four theme-bearing frontend files wholesale from the derived
 * DesignTokens + font pairing + logo:
 *
 *   frontend/src/index.css                  — every CSS custom property
 *   frontend/src/design-system/tokens.json  — the full token catalog agents read
 *   frontend/index.html                     — the Google Fonts <link> + favicon
 *   frontend/tailwind.config.ts             — colour/font utilities → the vars
 *
 * Pure: same input → same output, no disk writes. The token VALUES are the
 * derivePalette() output (light OR dark), so the rendered app, the design-system
 * catalog the AI agents read, and Tailwind's utilities can never drift.
 */

import {
  DEFAULT_BRAND,
  derivePalette,
  fontPairing,
  googleFontsUrl,
} from '@ghosty/branding';
import type { ScaffoldFile } from './scaffold.js';

/** The shape of @ghosty/branding's DesignTokens (duplicated to keep this package dependency-light). */
export interface ThemeDesignTokens {
  mode: 'light' | 'dark';
  bg: string;
  surface: string;
  surface2: string;
  line: string;
  text: string;
  muted: string;
  accent: string;
  accentHover: string;
  onAccent: string;
  secondary: string;
  onSecondary: string;
  success: string;
  warning: string;
  danger: string;
  focus: string;
}

/**
 * Corner-rounding personality. Drives the --radius-sm/md/lg CSS vars (and thus
 * the rounded / rounded-md / rounded-lg utilities the templates use).
 */
export type RadiusStyle = 'sharp' | 'rounded' | 'pill';

/** sm/md/lg radius values per style. 'rounded' equals Tailwind's defaults —
 * the values the templates were originally built against. */
const RADIUS_SCALES: Record<RadiusStyle, { sm: string; md: string; lg: string }> = {
  sharp: { sm: '0', md: '0.125rem', lg: '0.25rem' },
  rounded: { sm: '0.25rem', md: '0.375rem', lg: '0.5rem' },
  pill: { sm: '0.5rem', md: '0.75rem', lg: '1rem' },
};

/** Resolve a radius style (undefined → 'rounded', today's look) to its scale. */
export function radiusScale(style?: RadiusStyle): { sm: string; md: string; lg: string } {
  return RADIUS_SCALES[style ?? 'rounded'];
}

/** The full theme a producer derives (via @ghosty/branding) and passes in. */
export interface ScaffoldTheme {
  /** The derived design-token set (light or dark). */
  tokens: ThemeDesignTokens;
  /** CSS font-family stack for headings/UI, e.g. 'Fraunces, Georgia, serif'. */
  fontDisplayStack: string;
  /** CSS font-family stack for code/labels, e.g. '"IBM Plex Mono", monospace'. */
  fontMonoStack: string;
  /** The single Google Fonts stylesheet URL covering both faces. */
  fontGoogleUrl: string;
  /** Optional logo URL — used as the app favicon when present. */
  logoUrl?: string;
  /** Corner-rounding personality. Undefined → 'rounded' (today's defaults). */
  radius?: RadiusStyle;
}

/**
 * Ghosty's house brand as a ScaffoldTheme — the fallback used when a producer
 * doesn't pass a `theme` (older callers, tests). Derived from DEFAULT_BRAND via
 * @ghosty/branding so the scaffold is ALWAYS fully branded, never left with a
 * bare hardcoded palette.
 */
export function defaultTheme(): ScaffoldTheme {
  const tokens = derivePalette({
    mode: DEFAULT_BRAND.mode,
    primary: DEFAULT_BRAND.primary,
    secondary: DEFAULT_BRAND.secondary,
  });
  const fp = fontPairing(DEFAULT_BRAND.fontKey);
  return {
    tokens,
    fontDisplayStack: fp.display.stack,
    fontMonoStack: fp.mono.stack,
    fontGoogleUrl: googleFontsUrl(fp),
  };
}

/** One canonical token → CSS var name + the design-system role string. */
interface TokenSpec {
  key: keyof ThemeDesignTokens;
  cssVar: string;
  role: string;
}

/**
 * The canonical token contract (mirrors @ghosty/branding's DesignTokens and the
 * project's canonical CSS-var names). `mode` is metadata, not a colour var, so
 * it is excluded from the var/colour materialisation.
 */
const TOKEN_SPECS: TokenSpec[] = [
  { key: 'bg', cssVar: '--bg', role: 'page background' },
  { key: 'surface', cssVar: '--surface', role: 'cards, raised panels' },
  { key: 'surface2', cssVar: '--surface-2', role: 'nested / hover surfaces, inline code' },
  { key: 'line', cssVar: '--line', role: 'borders, dividers' },
  { key: 'text', cssVar: '--text', role: 'primary text' },
  { key: 'muted', cssVar: '--muted', role: 'secondary text, captions' },
  { key: 'accent', cssVar: '--accent', role: "the company's brand colour — highlights, status, links, primary action" },
  { key: 'accentHover', cssVar: '--accent-hover', role: 'hovered/active accent (buttons, links)' },
  { key: 'onAccent', cssVar: '--on-accent', role: 'readable text/icon colour ON an accent fill' },
  { key: 'secondary', cssVar: '--secondary', role: 'secondary brand colour' },
  { key: 'onSecondary', cssVar: '--on-secondary', role: 'readable text/icon colour ON a secondary fill' },
  { key: 'success', cssVar: '--success', role: 'success state' },
  { key: 'warning', cssVar: '--warning', role: 'warning state' },
  { key: 'danger', cssVar: '--danger', role: 'errors, destructive state' },
  { key: 'focus', cssVar: '--focus', role: 'focus ring' },
];

/** `--surface-2` → `surface-2`, the Tailwind utility / token-catalog key. */
const utilityName = (cssVar: string): string => cssVar.replace(/^--/, '');

/** Render the index.css value for one token, aligned for readability. */
function cssVarLine(spec: TokenSpec, tokens: ThemeDesignTokens): string {
  return `    ${spec.cssVar}: ${tokens[spec.key]}; /* ${spec.role} */`;
}

/**
 * frontend/src/index.css — emit EVERY CSS custom property from the derived
 * tokens (kebab-cased) on :root under @layer base, plus the font vars, set
 * color-scheme per mode, and drive body bg/text/font from the vars. No
 * hardcoded dark block survives.
 */
export function renderIndexCss(theme: ScaffoldTheme): string {
  const { tokens } = theme;
  const radii = radiusScale(theme.radius);
  const vars = TOKEN_SPECS.map((s) => cssVarLine(s, tokens)).join('\n');
  return `@tailwind base;
@tailwind components;
@tailwind utilities;

/*
 * Baked brand theme (mode: ${tokens.mode}). These CSS variables are the single
 * source of truth for the app's palette — Tailwind utilities (bg-bg, bg-surface,
 * text-text, text-accent, border-line, text-muted, text-danger, …) all resolve
 * to them via tailwind.config.ts.
 *
 * Values are the company's derived, accessibility-checked design tokens. They
 * MUST match src/design-system/tokens.json (the catalog the frontend agents
 * read). To restyle, change a value in BOTH places so the rendered screen and
 * the catalog never drift.
 */
@layer base {
  :root {
    color-scheme: ${tokens.mode};
${vars}
    --font-display: ${theme.fontDisplayStack};
    --font-mono: ${theme.fontMonoStack};
    --radius-sm: ${radii.sm};
    --radius-md: ${radii.md};
    --radius-lg: ${radii.lg};
  }

  body {
    background-color: var(--bg);
    color: var(--text);
    font-family: var(--font-display);
    -webkit-font-smoothing: antialiased;
  }
}
`;
}

/**
 * frontend/src/design-system/tokens.json — write the FULL derived token set
 * (every DesignTokens field, each with cssVar + tailwind utility + role) so the
 * AI frontend agents read accurate, on-brand values. Typography reflects the
 * chosen pairing. Spacing/radii/shadows/layout are theme-independent and kept.
 */
export function renderTokensJson(theme: ScaffoldTheme): string {
  const { tokens } = theme;
  const radii = radiusScale(theme.radius);
  const color: Record<string, unknown> = {};
  for (const spec of TOKEN_SPECS) {
    const util = utilityName(spec.cssVar);
    color[util] = {
      value: tokens[spec.key],
      cssVar: spec.cssVar,
      tailwind: util,
      role: spec.role,
    };
  }

  const doc = {
    $comment:
      "Raw design tokens — the single source of truth for the app's look. These are the company's derived, accessibility-checked brand tokens and mirror the CSS variables in src/index.css (which Tailwind utilities resolve to). When you change a value here, change it in index.css too so the rendered app and this catalog stay in sync. Agents: read this before generating UI; never invent values.",
    theme: `brand-${tokens.mode}`,
    mode: tokens.mode,
    color,
    typography: {
      fontFamily: {
        display: {
          value: theme.fontDisplayStack,
          cssVar: '--font-display',
          tailwind: 'font-sans',
          role: 'default UI + headings font (the app body is set in it)',
        },
        mono: {
          value: theme.fontMonoStack,
          cssVar: '--font-mono',
          tailwind: 'font-mono',
          role: 'code, timestamps, machine output',
        },
      },
      fontSize: {
        xs: { value: '0.75rem', tailwind: 'text-xs', role: 'captions, status pills, footer' },
        sm: { value: '0.875rem', tailwind: 'text-sm', role: 'body small, secondary copy' },
        base: { value: '1rem', tailwind: 'text-base', role: 'body' },
        lg: { value: '1.125rem', tailwind: 'text-lg', role: 'lead text, app title' },
        xl: { value: '1.25rem', tailwind: 'text-xl', role: 'section heading' },
        '2xl': { value: '1.5rem', tailwind: 'text-2xl', role: 'page heading' },
      },
      fontWeight: {
        normal: { value: '400', tailwind: 'font-normal' },
        medium: { value: '500', tailwind: 'font-medium' },
        semibold: { value: '600', tailwind: 'font-semibold' },
      },
      letterSpacing: {
        tight: { value: '-0.01em', tailwind: 'tracking-tight', role: 'headings' },
        normal: { value: '0', tailwind: 'tracking-normal' },
        wide: { value: '0.05em', tailwind: 'tracking-wide', role: 'uppercase labels' },
      },
    },
    spacing: {
      $comment:
        '4px base scale (Tailwind default). Use these step values for padding, margin, and gap; never raw pixels.',
      '1': { value: '0.25rem', tailwind: '1' },
      '2': { value: '0.5rem', tailwind: '2' },
      '3': { value: '0.75rem', tailwind: '3' },
      '4': { value: '1rem', tailwind: '4' },
      '5': { value: '1.25rem', tailwind: '5' },
      '6': { value: '1.5rem', tailwind: '6' },
      '8': { value: '2rem', tailwind: '8' },
      '12': { value: '3rem', tailwind: '12' },
    },
    radius: {
      $comment:
        "The app's corner-rounding personality. Values are baked from the brand's radius style and mirror --radius-sm/--radius-md/--radius-lg in src/index.css.",
      sm: { value: radii.sm, cssVar: '--radius-sm', tailwind: 'rounded' },
      md: { value: radii.md, cssVar: '--radius-md', tailwind: 'rounded-md' },
      lg: { value: radii.lg, cssVar: '--radius-lg', tailwind: 'rounded-lg' },
      full: { value: '9999px', tailwind: 'rounded-full', role: 'status dots, pills' },
    },
    shadow: {
      $comment: 'Prefer borders (border-line) over shadows for separation. Keep shadows subtle.',
      none: { value: 'none', tailwind: 'shadow-none' },
      sm: { value: '0 1px 2px 0 rgb(0 0 0 / 0.4)', tailwind: 'shadow-sm' },
      md: { value: '0 4px 12px 0 rgb(0 0 0 / 0.5)', tailwind: 'shadow-md', role: 'popovers, menus' },
    },
    layout: {
      contentWidth: { value: '48rem', tailwind: 'max-w-3xl', role: 'default reading column' },
      wideWidth: { value: '72rem', tailwind: 'max-w-6xl', role: 'dashboards, tables' },
      gutter: { value: '1.5rem', tailwind: 'px-6', role: 'horizontal page padding' },
    },
  };
  return `${JSON.stringify(doc, null, 2)}\n`;
}

/**
 * frontend/index.html — swap the templated Google Fonts <link> for the chosen
 * pairing's URL and, when a logoUrl is set, inject a favicon <link>. The
 * <title> ({{GHOSTY_APP_NAME}}) is already substituted upstream; we leave it.
 */
export function renderIndexHtml(template: string, theme: ScaffoldTheme): string {
  let html = template;

  const fontLink = `<link href="${theme.fontGoogleUrl}" rel="stylesheet" />`;
  // Replace the existing google-fonts stylesheet link (multi-line in the
  // template) with the pairing's URL; fall back to injecting before </head>.
  const fontLinkRe =
    /<link\s+[^>]*href="https:\/\/fonts\.googleapis\.com\/css2[^"]*"[^>]*rel="stylesheet"[^>]*\/?>/is;
  if (fontLinkRe.test(html)) {
    html = html.replace(fontLinkRe, fontLink);
  } else if (/<\/head>/i.test(html)) {
    html = html.replace(/<\/head>/i, `    ${fontLink}\n  </head>`);
  }

  if (theme.logoUrl) {
    const iconLink = `<link rel="icon" href="${theme.logoUrl}" />`;
    if (/<link\s+rel="icon"[^>]*\/?>/i.test(html)) {
      html = html.replace(/<link\s+rel="icon"[^>]*\/?>/i, iconLink);
    } else {
      // Inject right after the <title>…</title> when present (keeps it inside
      // <head> with clean indentation); else just before </head>.
      const titleRe = /(<title>[^<]*<\/title>)/i;
      if (titleRe.test(html)) {
        html = html.replace(titleRe, `$1\n    ${iconLink}`);
      } else if (/<\/head>/i.test(html)) {
        html = html.replace(/<\/head>/i, `    ${iconLink}\n  </head>`);
      }
    }
  }

  return html;
}

/**
 * frontend/tailwind.config.ts — map colour utilities to the CSS vars and the
 * font families to var(--font-display)/var(--font-mono). Canonical names
 * (bg/text/surface/...) plus legacy aliases (ink→bg, bone→text) so existing
 * template markup (bg-ink, text-bone) keeps rendering against the new palette.
 */
export function renderTailwindConfig(_theme: ScaffoldTheme): string {
  const colorLines: string[] = [];
  for (const spec of TOKEN_SPECS) {
    const util = utilityName(spec.cssVar);
    // Quote keys that aren't bare identifiers (surface-2, accent-hover, …).
    const key = /^[a-z][a-z0-9]*$/i.test(util) ? util : `'${util}'`;
    colorLines.push(`        ${key}: 'var(${spec.cssVar})',`);
  }
  // Legacy aliases so the starter markup (bg-ink / text-bone) keeps working.
  colorLines.push(`        // Legacy aliases — map the starter's ink/bone names onto the brand palette.`);
  colorLines.push(`        ink: 'var(--bg)',`);
  colorLines.push(`        bone: 'var(--text)',`);

  return `import type { Config } from 'tailwindcss';

/**
 * Theme values are CSS variables defined in src/index.css — change them there
 * (or override per-page) and every utility class follows. The colours and
 * fonts here mirror src/design-system/tokens.json (the catalog the frontend
 * agents read); keep the two in sync.
 */
const config: Config = {
  content: ['./index.html', './src/**/*.{ts,tsx}'],
  theme: {
    extend: {
      colors: {
${colorLines.join('\n')}
      },
      fontFamily: {
        // The app body is set in the brand's display face; mono for code.
        // Both are loaded in index.html and resolved from CSS vars.
        sans: ['var(--font-display)'],
        display: ['var(--font-display)'],
        mono: ['var(--font-mono)'],
      },
      borderRadius: {
        // The brand's corner-rounding personality, resolved from CSS vars —
        // rounded / rounded-md / rounded-lg follow the baked radius scale.
        DEFAULT: 'var(--radius-sm)',
        md: 'var(--radius-md)',
        lg: 'var(--radius-lg)',
      },
    },
  },
  plugins: [],
};

export default config;
`;
}

/** Posix paths of the four theme-bearing files, relative to the app root. */
const INDEX_CSS = 'frontend/src/index.css';
const TOKENS_JSON = 'frontend/src/design-system/tokens.json';
const INDEX_HTML = 'frontend/index.html';
const TAILWIND_CONFIG = 'frontend/tailwind.config.ts';

/**
 * Deterministic post-pass: rewrite the four theme-bearing frontend files in the
 * scaffold file list from the derived theme. Pure — returns a NEW sorted list,
 * never mutates the input or writes to disk. Files not present in the list
 * (e.g. a backend-only scaffold) are simply skipped.
 */
export function materializeTheme(files: ScaffoldFile[], theme: ScaffoldTheme): ScaffoldFile[] {
  const out = files.map((f) => {
    if (f.encoding !== 'utf8') return f;
    switch (f.path) {
      case INDEX_CSS:
        return { ...f, content: renderIndexCss(theme) };
      case TOKENS_JSON:
        return { ...f, content: renderTokensJson(theme) };
      case INDEX_HTML:
        return { ...f, content: renderIndexHtml(f.content, theme) };
      case TAILWIND_CONFIG:
        return { ...f, content: renderTailwindConfig(theme) };
      default:
        return f;
    }
  });
  return out.sort((a, b) => a.path.localeCompare(b.path));
}
